/**
 * FabricRenderer - Uniwersalny komponent renderujący Layout JSON na Fabric.js canvas.
 *
 * Tekst jest renderowany przez Fabric.js Textbox z splitByGrapheme: true,
 * co gwarantuje że żaden tekst nie przekroczy ustawionej szerokości.
 * Brak customowych funkcji do zmiany fontSize - rozmiar pochodzi z definicji layoutu.
 */

import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Canvas, Image, Textbox, Rect, Pattern } from 'fabric';
import jsPDF from 'jspdf';
import { parseTextDSL, checkCondition, parseColor } from '../textDSL/parser';
import { calculatePosition, calculateFontSize } from './positionCalculator';
import { loadFonts } from './fontLoader';

// Import domyślnych obrazków
import krzyz1 from '../../../../img/krzyz1.png';
import whiteCross from '../../../../img/whiteCross.png';
import crossPlaqueDefaultImage from '../../../../img/crossPlaqueDefaultImage.png';

// Mapa domyślnych obrazków
const defaultImages = {
    'krzyz1.png': krzyz1,
    'whiteCross.png': whiteCross,
    'crossPlaqueDefaultImage.png': crossPlaqueDefaultImage
};

const EMPTY_DATA = {};
const EMPTY_OVERRIDES = {};
const A4_ORIENTATION_BY_LAYOUT = {
    'obituary-minimalizm': 'landscape',
    'obituary-pogrubione': 'landscape',
    'obituary-black': 'landscape',
    'cross-plaque': 'portrait',
    'snitch-plaque': 'portrait',
    'coffin-plaque': 'portrait',
    'urn-plaque': 'portrait',
};
const JPG_EXPORT_SIZES = {
    'obituary-minimalizm': { width: 1754, height: 1240 },
    'obituary-pogrubione': { width: 1754, height: 1240 },
    'obituary-black': { width: 1754, height: 1240 },
    'cross-plaque': { width: 1240, height: 1754 },
    'snitch-plaque': { width: 846, height: 540 },
    'coffin-plaque': { width: 846, height: 540 },
    'urn-plaque': { width: 846, height: 540 },
};
const PDF_EXPORT_SIZES = {
    'obituary-minimalizm': { width: 1754, height: 1240 },
    'obituary-pogrubione': { width: 1754, height: 1240 },
    'obituary-black': { width: 1754, height: 1240 },
    'cross-plaque': { width: 1240, height: 1754 },
    'snitch-plaque': { width: 2093, height: 2160, contentLeft: 100, contentTop: 100 },
    'coffin-plaque': { width: 1216, height: 2160, contentLeft: 100, contentTop: 100, pdfScale: 1.05 },
    'urn-plaque': { width: 2093, height: 2160, contentLeft: 100, contentTop: 100 },
};

/**
 * Pobierz domyślny obrazek
 */
function getDefaultImage(imageName) {
    return defaultImages[imageName] || krzyz1;
}

/**
 * Załaduj obrazek jako element Fabric.Image
 */
async function loadImage(src, token) {
    let imageSrc = src;

    // Dodaj token jeśli to URL z backendu i token jeszcze nie jest w URL
    if (src && !src.startsWith('data:') && !src.startsWith('blob:') && !src.startsWith('/') && token && !src.includes('token=')) {
        imageSrc = src.includes('?') ? `${src}&token=${token}` : `${src}?token=${token}`;
    }

    try {
        const fabricImage = await Image.fromURL(imageSrc, { crossOrigin: 'anonymous' });
        return fabricImage;
    } catch (e) {
        console.warn(`Nie udało się załadować obrazka: ${imageSrc}`, e);
        return null;
    }
}

/**
 * Parsuje znaczniki **bold** w tekście.
 * Zwraca czysty tekst (bez **) oraz obiekt styles dla Fabric.js Textbox.
 */
function parseBoldMarkers(text) {
    const lines = text.split('\n');
    const cleanLines = [];
    const styles = {};

    lines.forEach((line, lineIndex) => {
        let cleanLine = '';
        let charOffset = 0;
        let remaining = line;

        while (remaining.length > 0) {
            const boldStart = remaining.indexOf('**');
            if (boldStart === -1) {
                cleanLine += remaining;
                break;
            }

            cleanLine += remaining.slice(0, boldStart);
            charOffset += boldStart;
            remaining = remaining.slice(boldStart + 2);

            const boldEnd = remaining.indexOf('**');
            if (boldEnd === -1) {
                cleanLine += remaining;
                break;
            }

            const boldText = remaining.slice(0, boldEnd);
            if (!styles[lineIndex]) styles[lineIndex] = {};
            for (let i = 0; i < boldText.length; i++) {
                styles[lineIndex][charOffset + i] = { fontWeight: 'bold' };
            }
            cleanLine += boldText;
            charOffset += boldText.length;
            remaining = remaining.slice(boldEnd + 2);
        }

        cleanLines.push(cleanLine);
    });

    const hasBold = Object.keys(styles).length > 0;
    return { cleanText: cleanLines.join('\n'), styles, hasBold };
}

/**
 * Renderuj pojedynczy element layoutu.
 *
 * Logika zawijania tekstu (odwzorowanie starych komponentów HTML/CSS):
 *
 * - singleLine: true → tekst NIGDY nie zawija się. fontSize maleje (Textbox.getLineWidth)
 *   aż cały tekst mieści się w jednej linii. Dolna granica: minFontSize.
 *
 * - singleLine: false/brak → tekst zawija się naturalnie na granicach słów
 *   (splitByGrapheme: false). Znaki \n w treści tworzą jawne łamanie linii
 *   (obsługiwane natywnie przez Fabric.js Textbox).
 */
async function renderElement(element, data, layout, editable, token, overrides = {}) {
    const { canvas: canvasDims, theme } = layout;
    const position = calculatePosition(element.position, canvasDims);

    if (element.type === 'text') {
        let parsedText;
        if (overrides[element.id] !== undefined && overrides[element.id] !== '') {
            parsedText = overrides[element.id];
        } else {
            parsedText = parseTextDSL(element.content || '', data);
        }

        if (!parsedText.trim()) return null;

        const { cleanText, styles: boldStyles, hasBold } = parseBoldMarkers(parsedText);
        const textToRender = hasBold ? cleanText : parsedText;

        const baseFontSize = calculateFontSize(element.style?.fontSize, canvasDims.width);
        const fill = parseColor(element.style?.fill, theme);
        const targetWidth = position.width || 200;
        const singleLine = element.style?.singleLine || false;
        const minFontSize = element.style?.minFontSize || 8;

        let finalFontSize = baseFontSize;

        if (singleLine) {
            // Pomiar jako jedna linia - duży width zapobiega zawijaniu przez Fabric
            const measure = new Textbox(textToRender, {
                width: 50000,
                fontSize: finalFontSize,
                fontFamily: element.style?.fontFamily || 'Arial',
                fontWeight: element.style?.fontWeight || 'normal',
                fontStyle: element.style?.fontStyle || 'normal',
            });
            measure.initDimensions();

            // Zmniejszaj fontSize aż tekst zmieści się w targetWidth
            while (measure.getLineWidth(0) > targetWidth && finalFontSize > minFontSize) {
                finalFontSize -= 1;
                measure.set('fontSize', finalFontSize);
                measure.initDimensions();
            }
        }

        const textbox = new Textbox(textToRender, {
            top: position.top,
            left: position.left,
            width: targetWidth,
            fontSize: finalFontSize,
            fontFamily: element.style?.fontFamily || 'Arial',
            fontWeight: element.style?.fontWeight || 'normal',
            fontStyle: element.style?.fontStyle || 'normal',
            textAlign: element.style?.textAlign || 'center',
            fill,
            splitByGrapheme: false,
            selectable: editable && element.editable !== false,
            editable: editable && element.editable !== false,
        });

        if (hasBold) {
            textbox.styles = boldStyles;
        }

        // Zmniejszaj fontSize aż tekst zmieści się w maxHeight
        if (element.style?.maxHeight) {
            const maxHeight = element.style.maxHeight;
            textbox.initDimensions();
            while (textbox.height > maxHeight && finalFontSize > minFontSize) {
                finalFontSize -= 1;
                textbox.set('fontSize', finalFontSize);
                textbox.initDimensions();
            }
        }

        return textbox;
    }

    if (element.type === 'image') {
        let imageSrc = parseTextDSL(element.content || '', data);

        if (!imageSrc || imageSrc === '') {
            const defaultMatch = element.content?.match(/\{[^|]+\|default:([^}]+)\}/);
            if (defaultMatch) {
                imageSrc = getDefaultImage(defaultMatch[1]);
            }
        }

        if (!imageSrc.startsWith('data:') && !imageSrc.startsWith('blob:') && !imageSrc.startsWith('http') && !imageSrc.startsWith('/')) {
            imageSrc = getDefaultImage(imageSrc);
        }

        const fabricImage = await loadImage(imageSrc, token);

        if (fabricImage) {
            fabricImage.set({
                top: position.top,
                left: position.left,
                selectable: editable && element.editable !== false
            });

            if (position.width) {
                fabricImage.scaleToWidth(position.width);
            }

            return fabricImage;
        }

        return null;
    }

    if (element.type === 'rect') {
        const rect = new Rect({
            top: position.top,
            left: position.left,
            width: position.width || 100,
            height: element.style?.height || 2,
            fill: element.style?.fill || '#000000',
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false,
        });
        return rect;
    }

    if (element.type === 'pattern') {
        const svgString = element.style?.svgString;
        const patternHeight = element.style?.patternHeight || 20;
        if (!svgString) return null;

        const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
        const img = new window.Image();
        await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
            img.src = dataUrl;
        });

        const pattern = new Pattern({ source: img, repeat: 'repeat-x' });
        const rect = new Rect({
            top: position.top,
            left: position.left,
            width: position.width || 100,
            height: patternHeight,
            fill: pattern,
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false,
        });
        return rect;
    }

    return null;
}

const FabricRenderer = forwardRef(({
    layout,
    data = EMPTY_DATA,
    templateUrl,
    editable = false,
    overrides = EMPTY_OVERRIDES,
    onElementSelect,
    onElementChange,
    className,
    style,
    isMirrorImage = false,
    fixedHeight = null,
}, ref) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const fabricCanvasRef = useRef(null);
    const isMountedRef = useRef(false);
    const isRenderingRef = useRef(false);
    const pendingRenderRef = useRef(false);
    const dataRef = useRef(data);
    const overridesRef = useRef(overrides);
    const [canvas, setCanvas] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [elementsMap, setElementsMap] = useState(new Map());
    const [scale, setScale] = useState(1);

    const token = localStorage.getItem('token');

    useEffect(() => { dataRef.current = data; }, [data]);
    useEffect(() => { overridesRef.current = overrides; }, [overrides]);

    const canvasWidth = layout?.canvas?.width || 1000;
    const canvasHeight = layout?.canvas?.height || 707;

    // Funkcja odbicia lustrzanego
    function mirrorImage(dataUrl, width, height) {
        return new Promise((resolve) => {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const ctx = tempCanvas.getContext('2d');

            const img = new window.Image();
            img.onload = () => {
                ctx.translate(width, 0);
                ctx.scale(-1, 1);
                ctx.drawImage(img, 0, 0);
                resolve(tempCanvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(dataUrl);
            img.src = dataUrl;
        });
    }

    function composeExportImage(dataUrl, {
        width,
        height,
        contentLeft = 0,
        contentTop = 0,
        type = 'image/png',
        quality = 1.0,
    }) {
        return new Promise((resolve) => {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const ctx = tempCanvas.getContext('2d');

            const img = new window.Image();
            img.onload = () => {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);

                const drawWidth = Math.min(img.width, width - contentLeft);
                const drawHeight = Math.min(img.height, height - contentTop);
                ctx.drawImage(img, contentLeft, contentTop, drawWidth, drawHeight);
                resolve(tempCanvas.toDataURL(type, quality));
            };
            img.onerror = () => resolve(dataUrl);
            img.src = dataUrl;
        });
    }

    async function exportCanvasImage({
        format = 'png',
        targetSize,
        multiplier = 4,
        quality = 1.0,
    } = {}) {
        if (!canvas) return null;

        const shouldKeepSourceSize = targetSize?.contentLeft !== undefined || targetSize?.contentTop !== undefined;
        const effectiveMultiplier = shouldKeepSourceSize
            ? 1
            : targetSize
            ? Math.max(canvasWidth ? targetSize.width / canvasWidth : 1, canvasHeight ? targetSize.height / canvasHeight : 1, 1)
            : multiplier;

        let dataUrl = canvas.toDataURL({
            format,
            quality,
            multiplier: effectiveMultiplier,
        });

        if (targetSize) {
            dataUrl = await composeExportImage(dataUrl, {
                ...targetSize,
                type: format === 'jpeg' ? 'image/jpeg' : 'image/png',
                quality,
            });
        }

        if (isMirrorImage) {
            const mirrorWidth = targetSize?.width || canvasWidth * effectiveMultiplier;
            const mirrorHeight = targetSize?.height || canvasHeight * effectiveMultiplier;
            dataUrl = await mirrorImage(dataUrl, mirrorWidth, mirrorHeight);
        }

        return dataUrl;
    }

    // Expose metody do rodzica przez ref
    useImperativeHandle(ref, () => ({
        getCanvas: () => canvas,

        toDataURL: (options) => canvas?.toDataURL(options),

        exportPNG: async (multiplier = 4) => {
            if (!canvas) return null;
            canvas.discardActiveObject();
            canvas.renderAll();

            return exportCanvasImage({ format: 'png', multiplier });
        },

        exportPDF: async (filename = 'dokument.pdf') => {
            if (!canvas) return;

            canvas.discardActiveObject();
            canvas.renderAll();

            const exportSize = PDF_EXPORT_SIZES[layout?.id] || {
                width: canvasWidth * 4,
                height: canvasHeight * 4,
            };
            const imgData = await exportCanvasImage({
                format: 'png',
                quality: 1.0,
                targetSize: exportSize,
            });
            const orientation = A4_ORIENTATION_BY_LAYOUT[layout?.id]
                || (layout?.canvas?.orientation === 'portrait' ? 'portrait' : 'landscape');
            const pdf = new jsPDF(orientation, 'pt', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const imgWidth = pdfWidth * (exportSize.pdfScale || 1);
            const imgHeight = imgWidth * (exportSize.height / exportSize.width);

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            pdf.save(filename);
        },

        exportJPG: async (filename = 'dokument.jpg') => {
            if (!canvas) return;

            canvas.discardActiveObject();
            canvas.renderAll();

            const imgData = await exportCanvasImage({
                format: 'jpeg',
                targetSize: JPG_EXPORT_SIZES[layout?.id],
                quality: 1.0,
            });

            const link = document.createElement('a');
            link.href = imgData;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        getElementValue: (elementId) => {
            const fabricObj = elementsMap.get(elementId);
            return fabricObj?.text || fabricObj?.getSrc?.() || null;
        },

        setElementValue: (elementId, value) => {
            const fabricObj = elementsMap.get(elementId);
            if (fabricObj && fabricObj.type === 'textbox') {
                fabricObj.set('text', value);
                canvas?.renderAll();
            }
        },

        rerender: () => {
            renderLayout();
        }
    }));

    // Renderowanie layoutu
    const renderLayout = useCallback(async () => {
        if (!canvas || !layout || !isMountedRef.current) return;

        if (isRenderingRef.current) {
            pendingRenderRef.current = true;
            return;
        }
        isRenderingRef.current = true;
        setIsLoading(true);

        await new Promise(resolve => setTimeout(resolve, 0));

        // Jeśli canvas został wymieniony (zmiana wymiarów) lub komponent odmontowany — przerwij
        if (!isMountedRef.current || !fabricCanvasRef.current || fabricCanvasRef.current !== canvas) {
            isRenderingRef.current = false;
            return;
        }

        canvas.clear();
        canvas.backgroundColor = '#fff';

        try {
            // Załaduj czcionki (z wagami normal+bold) PRZED renderowaniem elementów
            if (layout.theme?.fonts) {
                await loadFonts(layout.theme.fonts);
            }

            // Załaduj tło
            if (templateUrl) {
                const bgImage = await loadImage(templateUrl, token);
                if (bgImage && isMountedRef.current) {
                    bgImage.scaleToWidth(canvasWidth);
                    bgImage.scaleToHeight(canvasHeight);
                    const bgWidth = bgImage.getScaledWidth();
                    const bgHeight = bgImage.getScaledHeight();
                    canvas.backgroundImage = bgImage;
                    canvas.backgroundImage.set({
                        left: (canvasWidth - bgWidth) / 2,
                        top: (canvasHeight - bgHeight) / 2
                    });
                }
            }

            if (!isMountedRef.current) return;

            const currentData = dataRef.current;
            const currentOverrides = overridesRef.current;
            const mergedData = { ...currentData, ...currentOverrides };

            const newElementsMap = new Map();

            for (const element of layout.elements || []) {
                if (element.condition && !checkCondition(element.condition, mergedData)) {
                    continue;
                }

                const fabricObject = await renderElement(element, mergedData, layout, editable, token, currentOverrides);

                if (fabricObject) {
                    canvas.add(fabricObject);
                    newElementsMap.set(element.id, fabricObject);

                    fabricObject.elementId = element.id;
                    fabricObject.elementLabel = element.label;
                }
            }

            if (!isMountedRef.current) return;

            setElementsMap(newElementsMap);
            canvas.renderAll();
            setIsLoading(false);
        } finally {
            isRenderingRef.current = false;
            if (pendingRenderRef.current && isMountedRef.current) {
                pendingRenderRef.current = false;
                window.requestAnimationFrame(renderLayout);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, layout, templateUrl, editable, token, canvasWidth, canvasHeight]);

    // Inicjalizacja canvas
    useEffect(() => {
        isMountedRef.current = true;

        if (!containerRef.current || !layout) return;

        const cleanup = () => {
            if (fabricCanvasRef.current) {
                try {
                    fabricCanvasRef.current.dispose();
                } catch (e) {
                    console.warn('Error disposing canvas:', e);
                }
                fabricCanvasRef.current = null;
            }
            if (containerRef.current) {
                const canvasElements = containerRef.current.querySelectorAll('canvas, .canvas-container');
                canvasElements.forEach(el => el.remove());
            }
            setCanvas(null);
        };

        cleanup();

        const canvasEl = document.createElement('canvas');
        containerRef.current.appendChild(canvasEl);

        try {
            const initCanvas = new Canvas(canvasEl, {
                width: canvasWidth,
                height: canvasHeight,
                backgroundColor: '#fff',
                selection: editable,
                interactive: editable
            });

            if (!isMountedRef.current) {
                initCanvas.dispose();
                canvasEl.remove();
                return;
            }

            fabricCanvasRef.current = initCanvas;
            canvasRef.current = canvasEl;
            setCanvas(initCanvas);
        } catch (e) {
            console.error('Error initializing canvas:', e);
            canvasEl.remove();
        }

        return () => {
            isMountedRef.current = false;
            cleanup();
        };
    }, [canvasWidth, canvasHeight, editable, layout?.id]);

    // Renderowanie po inicjalizacji canvas
    useEffect(() => {
        if (canvas && layout) {
            renderLayout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, layout, templateUrl]);

    useEffect(() => {
        if (canvas && layout) {
            renderLayout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, overrides]);

    // Obsługa zdarzeń edycji
    useEffect(() => {
        if (!canvas || !editable) return;

        const handleSelection = (e) => {
            if (onElementSelect && e.selected?.[0]) {
                onElementSelect(e.selected[0].elementId, e.selected[0]);
            }
        };

        const handleSelectionCleared = () => {
            if (onElementSelect) {
                onElementSelect(null, null);
            }
        };

        const handleModified = (e) => {
            if (onElementChange && e.target) {
                onElementChange(e.target.elementId, {
                    text: e.target.text,
                    ...e.target.toObject()
                });
            }
        };

        canvas.on('selection:created', handleSelection);
        canvas.on('selection:updated', handleSelection);
        canvas.on('selection:cleared', handleSelectionCleared);
        canvas.on('object:modified', handleModified);
        canvas.on('text:changed', handleModified);

        return () => {
            canvas.off('selection:created', handleSelection);
            canvas.off('selection:updated', handleSelection);
            canvas.off('selection:cleared', handleSelectionCleared);
            canvas.off('object:modified', handleModified);
            canvas.off('text:changed', handleModified);
        };
    }, [canvas, editable, onElementSelect, onElementChange]);

    // Responsywne skalowanie
    useEffect(() => {
        if (!canvas || !containerRef.current) return;

        const resize = () => {
            let newScale;

            if (fixedHeight) {
                // Tryb szarfy: skaluj po wysokości, szerokość może wychodzić poza kontener (scroll)
                newScale = fixedHeight / canvasHeight;

                // Użyj canvas.setZoom + setDimensions — poprawne skalowanie Fabric.js bez CSS stretch
                const newWidth = Math.round(canvasWidth * newScale);
                const newHeight = Math.round(canvasHeight * newScale);
                canvas.setZoom(newScale);
                canvas.setDimensions({ width: newWidth, height: newHeight });
                containerRef.current.style.width = `${newWidth}px`;
                containerRef.current.style.height = `${newHeight}px`;
                // Bez centrowania — canvas szerszy niż maxWidth wychodzi poza ekran (negatywny offset)
                containerRef.current.style.justifyContent = 'flex-start';
                containerRef.current.style.maxWidth = 'none';
            } else {
                const wrapperWidth = containerRef.current.clientWidth;
                const wrapperHeight = containerRef.current.parentElement?.clientHeight || window.innerHeight * 0.6;
                const scaleByWidth = wrapperWidth / canvasWidth;
                const scaleByHeight = wrapperHeight / canvasHeight;
                newScale = Math.min(scaleByWidth, scaleByHeight, 1);

                const newWidth = canvasWidth * newScale;
                const newHeight = canvasHeight * newScale;

                if (canvas.lowerCanvasEl) {
                    canvas.lowerCanvasEl.style.width = `${newWidth}px`;
                    canvas.lowerCanvasEl.style.height = `${newHeight}px`;
                }

                const canvasContainer = containerRef.current.querySelector('.canvas-container');
                if (canvasContainer) {
                    canvasContainer.style.width = `${newWidth}px`;
                    canvasContainer.style.height = `${newHeight}px`;
                    canvasContainer.style.margin = '0 auto';
                }

                const upperCanvas = containerRef.current.querySelector('.upper-canvas');
                if (upperCanvas) {
                    upperCanvas.style.width = `${newWidth}px`;
                    upperCanvas.style.height = `${newHeight}px`;
                }

                containerRef.current.style.height = `${newHeight}px`;
                containerRef.current.style.width = '100%';
            }

            setScale(newScale);
            canvas.renderAll();
        };

        resize();
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, [canvas, canvasWidth, canvasHeight, fixedHeight]);

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                width: '100%',
                maxWidth: `${canvasWidth}px`,
                position: 'relative',
                margin: '0 auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                ...style
            }}
        >
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10
                }}>
                    Ładowanie...
                </div>
            )}
        </div>
    );
});

FabricRenderer.displayName = 'FabricRenderer';

export default FabricRenderer;
